import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import Badge from '../../components/shared/Badge/Badge';
import BtButton from '../bt-button/bt-button';
import { formatShortDay } from '../page-supplier-evaluations/reviewFormat';
import { UNEVALUATED_META } from '../../util/evaluationStatusMeta';
import { WinnerPurchase } from '../../services/service-evaluations';
import { WizardSeedSupplier } from '../../util/evaluationsNavigation';
import styles from './app-evaluation-overview.module.scss';

// The purchase is carried over only when there is no choice to make.
export const seedSupplier = (
  supplier: { _id: string; name: string },
  purchases: { areaId: string }[] = [],
): WizardSeedSupplier =>
  purchases.length === 1 ? { ...supplier, areaId: purchases[0].areaId } : supplier;

type Props = {
  title: string;
  sub?: string;
  purchases: WinnerPurchase[];
  /** Left out where no utvärdering can be started. */
  onEvaluate?: () => void;
};

const UnevaluatedLine: FC<Props> = ({ title, sub, purchases, onEvaluate }) => {
  const [t, i18n] = useTranslation('evaluations');

  const endText = (purchase: WinnerPurchase) =>
    purchase.endDay
      ? t('overview.unevaluated.endDate', 'Leveransslut {{date}}', {
          date: formatShortDay(purchase.endDay, i18n.language),
        })
      : t('overview.unevaluated.noEndDate', 'Inget leveransslut angivet');

  const ends =
    purchases.length === 1
      ? endText(purchases[0])
      : purchases.map(purchase => `${purchase.name}: ${endText(purchase)}`).join(' · ');

  return (
    <div className={styles.line}>
      <div className={styles.lineTitle}>
        <span className={styles.name} title={title}>
          {title}
        </span>
        {sub && <span className={`${styles.small} ${styles.muted}`}>{sub}</span>}
      </div>
      <div>
        <Badge color={UNEVALUATED_META.color} backgroundColor={UNEVALUATED_META.backgroundColor}>
          {t(UNEVALUATED_META.labelKey, UNEVALUATED_META.fallback)}
        </Badge>
      </div>
      <span className={`${styles.small} ${styles.muted} ${styles.lineWide}`}>{ends}</span>
      <div className={styles.lineAction}>
        {onEvaluate && (
          <BtButton color="white" size="xxs" tabIndex={0} onClick={onEvaluate}>
            {t('overview.unevaluated.evaluate', 'Utvärdera')}
          </BtButton>
        )}
      </div>
    </div>
  );
};

export default UnevaluatedLine;
